'use strict';

import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createUpdateApplicant, createSaveApplication, getYtjInformation } from '../../actions/application';
import { GetFetchOffer } from '../../actions/loanOffer';
import { createLoginToken } from '../../actions/login';
import { trackEvent } from '../../utils/gtm';
import BankSelectionPanel from '../BankSelectionPanel';
import { buildAttribution, campaignCodeFromUrl } from '../becomeApplicantV2/applicationContext';
import { normalizeBic } from '../becomeApplicantV2/bic';
import { Tick, TrustChips } from '../becomeApplicantV2/Trust';
import { TERMS_URL } from '../becomeApplicantV2/PageFrame';
import {
  AUTH_BYPASS, AUTH_CONTEXT_APPLICATION, fmt, validators, ERROR_TEXT, SAVE_FAILED_PHONE_MSG,
  resolveSavedApplicationId, focusAndScrollToFieldError,
  Range, Consent, Assurance, StepsBar, AttachmentsBlock, AuthButton, StickyCta, SuccessIcon, ArrowIcon, LockIcon,
  CompanyContactFields, CampaignCode, useProcessStarted, useStickyVisible, useAttachments, useBusinessId,
} from '../becomeApplicantV2/pieces';
import { LIMIT, FIXED_LOAN_LENGTH, FIXED_FREE_MONTHS, MONTHLY_RATE, monthlyInterest } from './limitContext';

/**
 * Luottolimiitti (credit limit), two steps — same structure as the loan form.
 *
 * Specifics carried over from the live limitLoan flow:
 *  - one slider: the limit amount 2 000–50 000 €; loan length 12 and 0 capital-free
 *    months are fixed and posted as such
 *  - the shown cost is the monthly interest instalment (2 %) on a fully used limit,
 *    charged only for months the limit is in use; no opening fee
 *  - loanApplicationLoanTypeEnum 2, GTM process name "credit_limit"
 *  - AI fast-track badge for limits up to 10 000 €
 *  - monthlyPayment is still taken from the offer API response, like the live flow does
 */

const PROCESS_NAME = 'credit_limit';
const CHIPS = ['0 € avausmaksu', 'Ilman vakuuksia', 'Päätös samana päivänä', '100 % suomalainen'];
const AI_FAST_TRACK_MAX = 10000;
const DEBOUNCE_MS = 300;

function LimitLoanV2Process({ context, application, offer, updateApplication, getOffer, fetchYtj, saveApplication, requestLoginToken, onStepChange }) {
  const prefilled = context.mode === 'prefilled';

  const [step, setStep] = useState(1); // 1 | 2 | 'done'
  // The page frame hides the cross-sell text under the form once the user has left step 1.
  useEffect(() => { if (onStepChange) onStepChange(step); }, [step, onStepChange]);
  const [limit, setLimit] = useState(context.limit);
  const urlCode = campaignCodeFromUrl(context.values); // ?koodi=… prefills the campaign code
  const [fields, setFields] = useState({ bic: '', companyName: '', phone: '', email: '', discountCode: urlCode });
  const [touched, setTouched] = useState({});
  const [terms, setTerms] = useState(false);
  const [showCampaign, setShowCampaign] = useState(Boolean(urlCode));
  const [showAttachments, setShowAttachments] = useState(false);
  const [editing, setEditing] = useState(!prefilled); // no URL parameter → slider open from the start
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [loadingServiceId, setLoadingServiceId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const attribution = useRef(null);
  const cardRef = useRef(null);
  const completed = useRef(false);
  const markStarted = useProcessStarted(PROCESS_NAME);
  const stickyVisible = useStickyVisible(cardRef, step);
  const { attachments, add: addFiles, remove: removeFile } = useAttachments(setSubmitError);
  const bic = useBusinessId({ fields, setFields, setErrors, application, updateApplication, fetchYtj });

  // Mount: attribution once, seed Redux, fetch the offer (used for monthlyPayment like the live flow).
  useEffect(() => {
    attribution.current = buildAttribution(context.values);
    updateApplication({
      loanAmount: limit, loanLength: FIXED_LOAN_LENGTH, capitalFreeMonths: FIXED_FREE_MONTHS,
      loanApplicationLoanTypeEnum: 2, loanApplicationTypeEnum: 0, ...attribution.current,
    });
    getOffer(limit, FIXED_LOAN_LENGTH, 0, FIXED_FREE_MONTHS);
  }, []); // mount only

  const firstOffer = useRef(true);
  useEffect(() => {
    if (firstOffer.current) { firstOffer.current = false; return undefined; }
    const t = setTimeout(() => getOffer(limit, FIXED_LOAN_LENGTH, 0, FIXED_FREE_MONTHS), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [limit, getOffer]);

  const changeLimit = (v) => { markStarted(); setLimit(v); };
  const setField = (name) => (e) => {
    markStarted();
    const value = name === 'bic' ? bic.onChange(e) : e.target.value;
    setFields((f) => ({ ...f, [name]: value }));
    if (errors[name] && (!validators[name] || validators[name](value))) setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const blurField = (name) => () => {
    setTouched((t) => ({ ...t, [name]: true }));
    if (name === 'bic') bic.onBlurNormalize();
    if (fields[name].trim() !== '' && validators[name] && !validators[name](fields[name])) {
      setErrors((er) => ({ ...er, [name]: ERROR_TEXT[name] }));
      trackEvent('field_error', { field_id: name, step: 1 });
    }
  };
  const isValid = (name) => touched[name] && validators[name] && validators[name](fields[name]);

  const monthlyPmt = (offer && offer.offerdata && offer.offerdata.loanDetails && offer.offerdata.loanDetails.monthlyPmt) || 0;

  // The API has only POST: a body without an id creates a new application, a body with the
  // id of an earlier save updates it. The live flow resends the id via Redux state; do the
  // same here so the step-1 save, the pre-authentication save and any back-and-forth all
  // land on one application instead of several.
  const buildPayload = () => ({
    ...(savedId ? { id: savedId } : {}),
    loanAmount: limit,
    loanLength: FIXED_LOAN_LENGTH,
    capitalFreeMonths: FIXED_FREE_MONTHS,
    LoanPurposeTypeEnum: 0,
    LoanGuaranteeTypeEnum: 0,
    LoanDueDateTypeEnum: 0,
    freeMonthsToPaymentsCheck: false,
    ApplicationSource: '',
    loanApplicationTypeEnum: 0,
    loanApplicationLoanTypeEnum: 2, // credit limit
    bic: normalizeBic(fields.bic),
    companyName: fields.companyName.trim(),
    companySsnBic: application.companySsnBic,
    businessLine: application.businessLine,
    address: application.address,
    addressCo: application.addressCo,
    zip: application.zip,
    city: application.city,
    phone: fields.phone.trim(),
    email: fields.email.trim(),
    discountCode: fields.discountCode.trim(),
    monthlyPayment: monthlyPmt,
    ...(attribution.current || {}),
  });

  const validateStep1 = () => {
    const next = {};
    Object.keys(validators).forEach((k) => { if (!validators[k](fields[k])) next[k] = ERROR_TEXT[k]; });
    if (!terms) next.terms = ERROR_TEXT.terms;
    setErrors(next);
    setTouched({ bic: true, companyName: true, phone: true, email: true });
    const firstBad = Object.keys(next)[0];
    if (firstBad) {
      trackEvent('form_submit_attempt', { step: 1, ok: false });
      focusAndScrollToFieldError(firstBad);
    }
    return !firstBad;
  };

  const scrollToCard = () => cardRef.current && cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const goStep2 = async () => {
    if (busy || !validateStep1()) return;
    setBusy(true);
    setSubmitError(null);
    const payload = buildPayload();
    updateApplication(payload);
    try {
      const saved = await saveApplication(payload);
      setSavedId(saved.id);
      if (saved.loanAmount > 0 && !completed.current) {
        completed.current = true; // going back and forward again must not report a second conversion
        trackEvent('kr_process_completed', { process_name: PROCESS_NAME, value: saved.loanAmount, application_id: saved.id });
      }
      localStorage.removeItem(`kr_process_started_${PROCESS_NAME}`);
    } catch (e) {
      trackEvent('kr_process_error', { process_name: PROCESS_NAME, error_message: e.message || 'save after step 1 failed' });
    } finally {
      setBusy(false);
      setStep(2);
      trackEvent('step_complete', { step: 1 });
      setTimeout(scrollToCard, 0);
    }
  };

  // Step 2: save with attachments, then hand over to the identity provider.
  // H1 mitigation (investigation 5.10.2026): merge Redux application (stage-1 API
  // response) so a partial update does not null out server-set fields.
  const authenticate = async (serviceId) => {
    if (busy) return;
    setBusy(true);
    setLoadingServiceId(serviceId);
    setSubmitError(null);
    let id = savedId;
    try {
      const saved = await saveApplication({ ...application, ...buildPayload(), attachments });
      id = resolveSavedApplicationId(saved, savedId);
      if (id) setSavedId(id);
    } catch (e) {
      trackEvent('kr_process_error', { process_name: PROCESS_NAME, error_message: e.message || 'save before authentication failed' });
      id = resolveSavedApplicationId(null, savedId);
    }
    if (!id) {
      setSubmitError(SAVE_FAILED_PHONE_MSG);
      setBusy(false); setLoadingServiceId(null);
      return;
    }
    setBankOpen(false);
    if (AUTH_BYPASS) {
      setBusy(false); setLoadingServiceId(null);
      setStep('done');
      setTimeout(scrollToCard, 0);
      return;
    }
    setTimeout(() => requestLoginToken(serviceId, AUTH_CONTEXT_APPLICATION, id), 500); // redirects the browser
  };

  /* ------------------------------------------------------------ texts */
  const kicker = step === 'done' ? 'Valmis'
    : step === 2 ? 'Vaihe 2 / 2 · Tunnistautuminen'
      : prefilled ? 'Vaihe 1 / 2 · Yrityksesi tiedot' : 'Vaihe 1 / 2 · Perustiedot';
  const title = step === 'done' ? '' : step === 2 ? 'Viimeistele hakemus' : 'Luottolimiitin avaus';

  /* ------------------------------------------------------------ render */
  const summary = (
    <div className="kr-loan-summary">
      <div className="kr-ls-head">
        <span className="kr-ls-label">Valitsemasi luottolimiitti</span>
        {prefilled && (
          <button type="button" className="kr-ls-edit" onClick={() => setEditing((v) => !v)}>{editing ? 'Valmis' : 'Muokkaa'}</button>
        )}
      </div>
      <div className="kr-ls-amount">{fmt(limit)} €</div>
      <div className="kr-ls-meta">limiittiluoton määrä <span className="kr-ls-dot">·</span> <strong>0 €</strong> avausmaksu</div>

      {editing && (
        <div className="kr-ls-sliders">
          <Range label="Haettavan limiittiluoton määrä" value={limit} range={LIMIT} unit="€" big format={fmt} ends={['2 000 €', '50 000 €']} onChange={changeLimit} />
        </div>
      )}

      <div className="kr-fin-rows">
        <div className="kr-fin-row">
          <div>
            <div className="kr-fr-label">Korkoerä kuukaudessa *</div>
            <div className="kr-fr-sub">Kun limiitti on käytössä täysimääräisesti · {MONTHLY_RATE * 100} % / kk</div>
          </div>
          <div className="kr-fr-val">{fmt(monthlyInterest(limit))} €</div>
        </div>
        <div className="kr-fin-row">
          <div>
            <div className="kr-fr-label">Avausmaksu</div>
            <div className="kr-fr-sub">Limiitin avaaminen ei maksa mitään</div>
          </div>
          <div className="kr-fr-val">0 €</div>
        </div>
      </div>
      {limit <= AI_FAST_TRACK_MAX && <div className="kr-ai-chip">⚡ Hakemus käsitellään AI-avusteisesti, voit saada lainatarjouksen minuutissa</div>}
    </div>
  );

  return (
    <>
      <section className="kr-hero">
        <div className="kr-container kr-hero-grid">
          <div className="kr-hero-lead">
            <h1>{prefilled ? 'Olet melkein valmis – täytä yrityksesi tiedot' : 'Avaa Kasvurahoituksen luottolimiitti'}</h1>
            <TrustChips items={CHIPS} />
          </div>

          <div className="kr-form-card" ref={cardRef}>
            <div className="kr-form-head">
              <div className="kr-kicker">{kicker}</div>
              {title && <h2>{title}</h2>}
            </div>
            <StepsBar step={step} />

            {step === 1 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 1">
                {summary}
                <p className="kr-calc-note">*) Luottolimiitissä maksat kuukausikorkoa vain niiltä kuukausilta, kun limiittiä on käytössä. Lopulliset ehdot tarkentuvat riskiarviomme perusteella.</p>
                <hr className="kr-hr" />

                <p className="kr-form-intro">Täytä yrityksen perustiedot huolellisesti limiitin avaamista varten. Voit muuttaa limiittiluoton määrää vielä myöhemmin.</p>

                <CompanyContactFields fields={fields} setField={setField} blurField={blurField} errors={errors} isValid={isValid} bic={bic} />
                <CampaignCode open={showCampaign} onToggle={() => setShowCampaign((v) => !v)} value={fields.discountCode} onChange={setField('discountCode')} />

                {/* Same consent text as the live limit form. */}
                <Consent id="kr-terms" checked={terms} onChange={(v) => { setTerms(v); if (v) setErrors((e) => ({ ...e, terms: undefined })); }} error={errors.terms}>
                  Hyväksyn Kasvurahoituksen <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">käyttöehdot</a>.
                  Annan suostumuksen tietojen noutamiseen ja käsittelyyn luottotietorekisteristä sekä muista saatavilla olevista tietolähteistä.
                  Hyväksyn, että hakemusta käsitellään AI-avusteisesti.
                </Consent>

                <button type="button" className="kr-btn kr-btn-primary" id="kr-toStep2" onClick={goStep2} disabled={busy}>
                  {busy ? <span className="kr-spinner" /> : <>Avaa luottolimiitti<ArrowIcon /></>}
                </button>
                <div className="kr-cta-note"><Tick /> Hakemus n. 2 min · Päätös samana päivänä · Maksuton</div>
                <Assurance items={['Tunnistautuminen vasta seuraavassa vaiheessa.', 'Tietosi käsitellään turvallisesti ja luottamuksellisesti.']} />
              </div>
            )}

            {step === 2 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 2: Tunnistautuminen">
                <p className="kr-auth-intro">
                  Pyydämme vielä tunnistautumaan pankkitunnuksilla tai mobiilivarmenteella.{' '}
                  <strong>Tarjouksen pyytäminen on maksutonta eikä sido yritystäsi vielä mihinkään.</strong>
                </p>
                {AUTH_BYPASS && <div className="kr-notice">Kehitystila: tunnistautuminen ohitetaan, hakemus tallennetaan mock-palvelimelle.</div>}
                {submitError && <div className="kr-form-error" role="alert">{submitError}</div>}

                <AttachmentsBlock open={showAttachments} onToggle={() => setShowAttachments((v) => !v)} attachments={attachments} onAdd={addFiles} onRemove={removeFile} />
                <AuthButton busy={busy} onClick={() => setBankOpen(true)} />

                <div className="kr-cta-note"><LockIcon /> Vahva tunnistautuminen, tiedot salataan</div>
                <Assurance items={['Saat tarjouksen ennen kuin teet päätöksen.', 'Asiakaspalvelumme auttaa tarvittaessa, ark. 9–16.']} />
                <button type="button" className="kr-back-link" onClick={() => { setStep(1); setTimeout(scrollToCard, 0); }} disabled={busy}>← Takaisin tietoihin</button>
              </div>
            )}

            {step === 'done' && (
              <div className="kr-success" role="status" aria-live="polite">
                <SuccessIcon />
                <h2>Lainahakemuksesi on rekisteröity ja otettu käsittelyyn</h2>
                {savedId && <p className="kr-success-ref">Hakemusnumero <strong>{savedId}</strong>{fields.companyName ? ` · ${fields.companyName}` : ''} · luottolimiitti {fmt(limit)} €</p>}
                <ol className="kr-next-steps">
                  <li>Olemme yhteydessä yhden arkipäivän kuluessa mahdollisissa hakemuksen käsittelyyn liittyvissä kysymyksissä.</li>
                  <li>Saat tarjouksen sähköpostiin <strong>{fields.email}</strong> ja tekstiviestillä.</li>
                </ol>
                <p><strong>Kysyttävää?</strong> Soita <a href="tel:+358968298003">09 6829 8003</a> (ark. 9–16).</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <BankSelectionPanel open={bankOpen} onClose={() => setBankOpen(false)} onBankSelect={authenticate} disabled={busy} loadingServiceId={loadingServiceId} />

      {step !== 'done' && <StickyCta visible={stickyVisible} label={step === 2 ? 'Viimeistele tunnistautumalla' : 'Siirry hakemukseen'} onClick={scrollToCard} />}
    </>
  );
}

const mapStateToProps = (state) => ({ application: state.application, offer: state.offer });

const mapDispatchToProps = (dispatch) => ({
  updateApplication: (application) => dispatch(createUpdateApplicant(application)),
  getOffer: (amt, len, due, free) => dispatch(GetFetchOffer(amt, len, due, free)),
  fetchYtj: (bic) => dispatch(getYtjInformation(bic)),
  saveApplication: (application) => dispatch(createSaveApplication(application)),
  requestLoginToken: (serviceId, authContext, applicationId) => dispatch(createLoginToken(serviceId, authContext, applicationId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LimitLoanV2Process);
